import { BACKEND_URL } from "../../config";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const userApi = createApi({
  reducerPath: "userApi",
  keepUnusedDataFor: 0,
  refetchOnMountOrArgChange: true,
  tagTypes: ["MyLeads", "EventDefaults", "BookingCalendar"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${BACKEND_URL}/user/portal/`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        // include token in req header
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
    fetchFn: async (input, init) => {
      const response = await fetch(input, init);
      if (response.status === 204) {
        return new Response(JSON.stringify([]), { status: 200 }); // Transform 204 to an empty array
      }
      return response;
    },
  }),
  endpoints: (builder) => ({
    getAllResourcesDetails: builder.query({
      query: () => `get-all-resources`,
    }),
    getPestroutesAccounts: builder.query({
      query: () => `get-kindred-employee-logins`,
    }),
    resetFrPassword: builder.mutation({
      query: ({ accountRecId, pestRoutesId, location, newPassword }) => ({
        url: "reset-fr-password",
        method: "POST",
        body: { accountRecId, pestRoutesId, location, newPassword },
      }),
    }),
    // `scope` is opt-in and changes the answer, not just the size of it:
    //   omitted / "active" -> the current season's still-open roster (what the
    //     Event invite picker has always used here).
    //   "all"              -> the wide set the Deduction / Reimbursement form
    //     needs: a deduction is recorded against a season, and a season that has
    //     since closed still has to be selectable (thread 1a0d38692f223e5b).
    //     Since 2026-10-06 that set is bounded to the current season and later
    //     (thread 1a112e638151ceea): a past season's backends are already
    //     settled, so an entry written against one never reaches a paycheck.
    // The token stays "all" rather than the newer "current-and-future" alias
    // because an unknown scope does NOT error in the BFF — it falls through to
    // the invite-picker branch — so a portal-ui shipping first under the new
    // name would silently shrink this picker to the ~6 still-open rows. "all" is
    // the wide query in both BFF versions; the form filters past seasons itself,
    // so either half is correct whichever deploys first.
    // The two args are separate RTK cache entries, so the invite picker keeps
    // its own filtered set. (9/24/26 - 1:11 PM; windowed scope 10/6/26 - 8:41 PM UTC)
    getRepSummer: builder.query({
      query: (params = {}) =>
        params?.scope ? `get-rep-summer?scope=${encodeURIComponent(params.scope)}` : `get-rep-summer`,
    }),
    getTaskStatus: builder.query({
      query: () => `get-task-status`,
      keepUnusedDataFor: Infinity,
    }),
    getDownlineReps: builder.query({
      query: (year) => `get-my-downline-reps${year ? `?year=${year}` : ""}`,
    }),
    getLeads: builder.query({
      query: ({ type, from, to } = {}) => {
          const params = new URLSearchParams();
          if (type) params.append("type", type);
          if (from) params.append("from", from);
          if (to)   params.append("to", to);
          const qs = params.toString();
          return `get-my-leads${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["MyLeads"],
    }),
    updateLeadStatus: builder.mutation({
      query: ({ recordId, status }) => ({
        url: `leads/${recordId}/status`,
        method: "PATCH",
        body: { status },
      }),
      // Optimistic update: immediately reflect the new status in the cache so
      // the status pill changes on tap without waiting for the server round-trip.
      // If the mutation fails the patch is rolled back automatically.
      onQueryStarted: async ({ recordId, status }, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          userApi.util.updateQueryData("getLeads", {}, (draft) => {
            const lead = draft?.data?.find((l) => l.id === recordId);
            if (lead) lead.status = status;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: ["MyLeads"],
    }),
    //! The My Friends Notifications switch (thread 1a1844a91564dba8). One Friend
    //! from the row's toggle, many from the bulk bar. OFF = the office sends this
    //! person no automated texts (events, tournaments, mass messages, ...).
    setFriendNotifications: builder.mutation({
      query: ({ employeeRecIds, enabled }) => ({
        url: "my-friends/notifications",
        method: "PATCH",
        body: { employeeRecIds, enabled },
      }),
      invalidatesTags: ["MyLeads"],
    }),
    updateLeadNotes: builder.mutation({
      query: ({ recordId, notes }) => ({
        url: `leads/${recordId}/notes`,
        method: "PATCH",
        body: { notes },
      }),
      invalidatesTags: ["MyLeads"],
    }),
    // Edit a friend's contact details from the My Friends "Edit" modal. `body`
    // carries only the fields being changed (firstName, lastName, phoneNumber,
    // email, leadSource, notes, interestLevel); the BFF writes just the keys it
    // receives, and for interestLevel `null` means "clear the answer" while an
    // absent key means "not given".
    updateLead: builder.mutation({
      query: ({ recordId, ...body }) => ({
        url: `leads/${recordId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["MyLeads"],
    }),
    updatePaymentAccount: builder.mutation({
      query: (payload) => ({
        url: 'update-payment-account',
        method: 'POST',
        body: payload,
      }),
    }),

    // Lead self-booking — PR-3-ui. The caller's saved Event defaults (or null).
    // These pre-fill CreateEvent and are auto-applied when a Google booking
    // mirrors into an Event. Response { status, message, data }.
    getEventDefaults: builder.query({
      query: () => `event-defaults`,
      providesTags: ["EventDefaults"],
    }),

    // PR-3-ui. Upsert the caller's Event defaults — the settings page sends the
    // complete current state on every save.
    putEventDefaults: builder.mutation({
      query: (payload) => ({
        url: `event-defaults`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["EventDefaults"],
    }),

    // Lead self-booking — PR-4. The caller's registered booking calendar(s).
    // Each maps a Google Appointment-Schedule calendar to the caller so its
    // bookings mirror into Events stamped with them. Response data is an array.
    getBookingCalendar: builder.query({
      query: () => `booking-calendar`,
      providesTags: ["BookingCalendar"],
    }),

    // Feature #9 — named booking-calendar picker. The caller's OWN Google
    // calendars ([{id, summary, primary}]) so the registration UI can show a
    // named dropdown instead of a raw calendar-ID paste box. Read-only; the BFF
    // soft-fails to an empty array for reps on a non-domain account, which the
    // UI treats as "fall back to the paste box."
    getGoogleCalendars: builder.query({
      query: () => `google-calendars`,
    }),

    // PR-4. Register/update one of the caller's booking calendars.
    putBookingCalendar: builder.mutation({
      query: (payload) => ({
        url: `booking-calendar`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["BookingCalendar"],
    }),

    // PR-4. Remove one of the caller's booking calendars (by calendarId).
    deleteBookingCalendar: builder.mutation({
      query: (calendarId) => ({
        url: `booking-calendar`,
        method: "DELETE",
        body: { calendarId },
      }),
      invalidatesTags: ["BookingCalendar"],
    }),

    // Availability read check. Upcoming items (times only) on the caller's
    // registered booking calendar(s) so a rep can confirm the system reads their
    // calendar before inviting any lead. Fetched on demand (lazy) off a button
    // — never on every render — so it can't add a Google API call to page load.
    getAvailabilityPreview: builder.query({
      query: () => `availability-preview`,
    }),
    // Live "Test connection" probe for the My Event Defaults health badge.
    // Read-only — does a single lightweight Google read on the BFF to confirm
    // the watcher credential can actually see the rep's registered calendar.
    // Exposed as a LAZY query so it never fires on mount/render (the badge's
    // initial state comes from getBookingCalendar); it runs only when the rep
    // clicks "Test connection". Response { status, message, data:
    // { registered, connected, calendarSummary?, timeZone?, error? } }.
    getBookingCalendarHealth: builder.query({
      query: () => `booking-calendar/health`,
    }),

    // Phase 2a (Friend Arena access) — generate a rep-scoped Arena invite link
    // to share "far and wide." Lazy so it only fires when the rep taps "Arena
    // invite," never on render. Response { status, message, data: { url } }.
    getArenaInviteLink: builder.query({
      query: () => `arena-invite/link`,
    }),

    // My Friends Admin — all org leads for positions with "My Friends Admin"
    // checked. Returns 403 when the caller's position lacks the flag, which the
    // UI uses to hide the Admin tab for non-admins.
    getAllLeadsAdmin: builder.query({
      query: () => `leads/all`,
      providesTags: ["MyLeads"],
    }),

    // My Friends Admin — EVERY Friend (fantasy_friend) regardless of who or how
    // they were added (thread #1217, Levi 2026-08-26). Same "My Friends Admin"
    // gate as getAllLeadsAdmin. Rendered in the Admin tab's "Friends" view.
    getAllFriendsAdmin: builder.query({
      query: () => `my-friends/all`,
      providesTags: ["MyLeads"],
    }),

    // Friend transfer (thread #1733 r2, Levi 2026-10-22) — the two calls behind
    // the Admin tab's Owner control.
    //
    // getFriendOwnerOptions: the reps a Friend can be handed to (active staff,
    // Friends excluded — a Friend's own employee record is not a candidate owner,
    // and there are ~1,400 of them). Lazy: fetched the first time an admin opens
    // a picker, not on page load.
    getFriendOwnerOptions: builder.query({
      query: () => `my-friends/admin/owners`,
    }),

    // transferFriendOwner: one write, immediate. Under FRIEND_STORE_FLIP the Mine
    // tab is a filter on the profile row's owner, so nothing is queued and nothing
    // needs a second step — invalidating "MyLeads" re-renders both tabs from the
    // row that already changed.
    transferFriendOwner: builder.mutation({
      query: ({ employeeRecId, friendId, ownerRepRecId }) => ({
        url: `my-friends/admin/transfer`,
        method: "POST",
        body: { employeeRecId, friendId, ownerRepRecId },
      }),
      invalidatesTags: ["MyLeads"],
    }),
  }),
});

export const { useGetAllResourcesDetailsQuery, useGetRepSummerQuery, useGetTaskStatusQuery, useUpdatePaymentAccountMutation, useGetDownlineRepsQuery, useGetLeadsQuery, useUpdateLeadStatusMutation, useUpdateLeadNotesMutation, useUpdateLeadMutation, useGetPestroutesAccountsQuery, useResetFrPasswordMutation, useGetEventDefaultsQuery, usePutEventDefaultsMutation, useGetBookingCalendarQuery, useGetGoogleCalendarsQuery, usePutBookingCalendarMutation, useDeleteBookingCalendarMutation, useLazyGetAvailabilityPreviewQuery, useLazyGetBookingCalendarHealthQuery, useLazyGetArenaInviteLinkQuery, useGetAllLeadsAdminQuery, useGetAllFriendsAdminQuery, useGetFriendOwnerOptionsQuery, useLazyGetFriendOwnerOptionsQuery, useTransferFriendOwnerMutation, useSetFriendNotificationsMutation } = userApi;
